/**
 * Shared utilities for formatting Slovak legal reference regulation labels.
 * Used by DecisionMetadata and LegislationMetadata sidebar components.
 */

import { documentSlug } from './documentMetadata';

// Reverse of backend _LEGISLATION_ABBREVIATIONS in html_renderer.py
// cspell:disable
const LEGISLATION_IRI_TO_ABBR: Record<string, string> = {
  'SK/ZZ/2015/160': 'CSP', // Civilný sporový poriadok
  'SK/ZZ/2015/161': 'CMP', // Civilný mimosporový poriadok
  'SK/ZZ/2015/162': 'SSP', // Správny súdny poriadok
  'SK/ZZ/1964/40': 'OZ', // Občiansky zákonník
  'SK/ZZ/1991/513': 'ObchZ', // Obchodný zákonník
  'SK/ZZ/2001/311': 'ZP', // Zákonník práce
  'SK/ZZ/2005/301': 'TP', // Trestný poriadok
  'SK/ZZ/2005/300': 'TZ', // Trestný zákon
  'SK/ZZ/1963/99': 'OSP', // Občiansky súdny poriadok (legacy)
  'SK/ZZ/1995/233': 'EP', // Exekučný poriadok
};
// cspell:enable

/**
 * Parse a Slov-lex URL fragment or IRI path suffix into Slovak legal shorthand.
 *
 * Accepts:
 * - Hash fragment:  "#paragraf-421.odsek-1.pismeno-b" → "§ 421 ods. 1 písm. b)"
 * - IRI path:       "/paragraf-421/odsek-1/pismeno-b" → "§ 421 ods. 1 písm. b)"
 *
 * Both formats use `key-value` segments (e.g. `paragraf-421`) separated
 * by `.` (hash) or `/` (IRI path).
 */
export function parseFragmentToLabel(fragmentOrPath: string): string | null {
  try {
    // Strip leading # or /
    const stripped = fragmentOrPath.replace(/^[#/]+/, '');
    if (!stripped) return null;

    // Split on . or / — both serve as part separators in their formats
    const parts = stripped.split(/[./]/).filter(Boolean);
    const formatted: string[] = [];

    for (const part of parts) {
      const dashIdx = part.indexOf('-');
      if (dashIdx === -1) continue;
      const key = part.slice(0, dashIdx);
      const value = part.slice(dashIdx + 1);
      if (!value) continue;

      switch (key) {
        case 'paragraf':
          formatted.push(`§ ${value}`);
          break;
        case 'clanok':
          formatted.push(`čl. ${value}`);
          break;
        case 'odsek':
          formatted.push(`ods. ${value}`);
          break;
        case 'pismeno':
          formatted.push(`písm. ${value})`);
          break;
        case 'bod':
          formatted.push(`bod ${value}`);
          break;
        case 'oznacenie':
          break; // skip structural markers
        default:
          break;
      }
    }

    return formatted.length > 0 ? formatted.join(' ') : null;
  } catch {
    return null;
  }
}

/**
 * Build the internal Lexomat URL for a decision reference regulation,
 * correctly converting section info into a hash fragment for deep-linking.
 *
 * Handles both data sources:
 * - IRI path:    "/SK/ZZ/2015/160/paragraf-421/odsek-1/pismeno-b"
 *                → "/sk/dokument/SK/ZZ/2015/160#paragraf-421.odsek-1.pismeno-b"
 * - Slov-lex URL: "https://...SK/ZZ/2015/160/#paragraf-421.odsek-1.pismeno-b"
 *                → "/sk/dokument/SK/ZZ/2015/160#paragraf-421.odsek-1.pismeno-b"
 *
 * Falls back to '#' if the URL cannot be parsed.
 */
export function buildRegulationHref(url: string, locale: string): string {
  if (!url) return '#';

  const iriMatch = url.match(/SK\/ZZ\/(\d+)\/(\d+)/i);
  if (!iriMatch) return '#';

  const year = iriMatch[1]!;
  const number = iriMatch[2]!;
  const iriKey = `SK/ZZ/${year}/${number}`;

  // Extract section as a dot-separated hash fragment
  let fragment = '';
  const hashIdx = url.indexOf('#');
  if (hashIdx !== -1) {
    // Slov-lex format: hash is already dot-separated (#paragraf-421.odsek-1.pismeno-b)
    fragment = url.slice(hashIdx);
  } else {
    // IRI path format: convert /paragraf-421/odsek-1/pismeno-b → #paragraf-421.odsek-1.pismeno-b
    const iriEnd = url.indexOf(iriKey) + iriKey.length;
    const pathSuffix = url.slice(iriEnd).replace(/^\/+/, '');
    if (pathSuffix) {
      fragment = `#${pathSuffix.replace(/\//g, '.')}`;
    }
  }

  const slug = documentSlug(locale);
  return `/${locale}/${slug}/${iriKey}${fragment}`;
}

/**
 * The next-intl typed pathname for the legislation document route
 * (`/document/[...segments]` ↔ `/dokument/[...segments]`). Targets are built as
 * `{ pathname: DOC_SEGMENTS_PATHNAME, params: { segments } }` so the locale
 * prefix is added by next-intl's <Link> rather than hardcoded.
 */
const DOC_SEGMENTS_PATHNAME = '/document/[...segments]' as const;

/**
 * A next-intl <Link> href target for the legislation document route.
 */
interface DocSegmentsHref {
  pathname: typeof DOC_SEGMENTS_PATHNAME;
  params: { segments: string[] };
}

/**
 * Convert a human Slovak section label into Slov-lex path segments.
 *
 *   "§ 255 ods. 1 písm. b)" → ["paragraf-255", "odsek-1", "pismeno-b"]
 *   "čl. 12 ods. 3"          → ["clanok-12", "odsek-3"]
 *
 * Returns an empty array when no structural component can be parsed, so callers
 * can link to the law root without inventing a broken section anchor.
 */
export function sectionLabelToSegments(label: string): string[] {
  if (!label) return [];

  const parts: { index: number; segment: string }[] = [];
  const push = (re: RegExp, prefix: string) => {
    const m = re.exec(label);
    if (m && m[1]) {
      parts.push({
        index: m.index,
        segment: `${prefix}-${m[1].toLowerCase()}`,
      });
    }
  };

  push(/§\s*(\d+[a-z]?)/i, 'paragraf');
  push(/čl\.\s*(\d+[a-z]?)/i, 'clanok');
  push(/ods\.\s*(\d+)/i, 'odsek');
  push(/písm\.\s*([a-z]+)/i, 'pismeno');
  push(/bod\s*(\d+)/i, 'bod');

  parts.sort((a, b) => a.index - b.index);
  return parts.map((p) => p.segment);
}

/**
 * Convert a human Slovak section label into a Slov-lex hash fragment
 * (dot-joined form of {@link sectionLabelToSegments}). Inverse of
 * {@link parseFragmentToLabel}. Returns null when nothing parses.
 */
export function buildSectionFragmentFromLabel(label: string): string | null {
  const segments = sectionLabelToSegments(label);
  return segments.length ? segments.join('.') : null;
}

/**
 * Build a next-intl <Link> target from a free-text regulation string found in
 * `citation_metadata.referenced_regulations` — e.g.
 * "311/2001 Z. z. § 59 ods. 1 písm. b)". Only the `number/year` is reliably
 * present; when a section tail exists it is appended as path segments so the
 * link lands on the exact provision.
 *
 * Returns null when no `number/year` can be extracted, so the caller can render
 * a non-clickable chip instead of a dead link.
 */
export function buildRegulationDocHref(raw: string): DocSegmentsHref | null {
  if (!raw) return null;

  const match = raw.match(/(\d{1,4})\s*\/\s*(\d{4})/);
  if (!match) return null;

  const number = match[1]!;
  const year = match[2]!;
  // number/year never matches the § patterns, so section parsing is safe here.
  const segments = ['SK', 'ZZ', year, number, ...sectionLabelToSegments(raw)];
  return { pathname: DOC_SEGMENTS_PATHNAME, params: { segments } };
}

/**
 * Build a next-intl <Link> target for a specific cited section of a law whose
 * IRI is already known (e.g. "/SK/ZZ/2001/311"). Appends the section path
 * segments when the label parses. Returns null when the IRI is missing/empty.
 */
export function buildCitedSectionDocHref(
  sectionLabel: string,
  iri: string | undefined
): DocSegmentsHref | null {
  if (!iri) return null;
  const iriSegments = iri.replace(/^\/+/, '').split('/').filter(Boolean);
  if (!iriSegments.length) return null;
  const segments = [...iriSegments, ...sectionLabelToSegments(sectionLabel)];
  return { pathname: DOC_SEGMENTS_PATHNAME, params: { segments } };
}

/**
 * Build a next-intl <Link> target for a law root from its IRI
 * (e.g. "/SK/ZZ/2001/311"). Returns null when the IRI is missing/empty.
 */
export function buildLawRootDocHref(
  iri: string | undefined
): DocSegmentsHref | null {
  if (!iri) return null;
  const iriSegments = iri.replace(/^\/+/, '').split('/').filter(Boolean);
  if (!iriSegments.length) return null;
  return { pathname: DOC_SEGMENTS_PATHNAME, params: { segments: iriSegments } };
}

/**
 * Format a decision reference regulation entry into a unique, descriptive label.
 *
 * Handles both data sources emitted by the backend:
 * - Scraper:  url = "https://slov-lex.sk/.../SK/ZZ/2015/160/#paragraf-421.odsek-1.pismeno-b"
 * - Regex:    url = "/SK/ZZ/2015/160/paragraf-421/odsek-1/pismeno-b"
 *
 * Output: "Zákon č. 160/2015 Z. z. (CSP), § 421 ods. 1 písm. b)"
 * Falls back to the raw name if the URL cannot be parsed.
 */
export function formatDecisionRegulationLabel(
  name: string,
  url: string
): string {
  if (!url) return name;

  // Extract year/number from the URL (present in both Slov-lex and IRI formats)
  const iriMatch = url.match(/SK\/ZZ\/(\d+)\/(\d+)/i);
  if (!iriMatch) return name;

  const year = iriMatch[1]!;
  const number = iriMatch[2]!;
  const iriKey = `SK/ZZ/${year}/${number}`;
  const abbr = LEGISLATION_IRI_TO_ABBR[iriKey];

  // Extract section label — try URL hash fragment first (Slov-lex format)
  let sectionLabel: string | null = null;
  const hashIdx = url.indexOf('#');
  if (hashIdx !== -1) {
    sectionLabel = parseFragmentToLabel(url.slice(hashIdx));
  }

  // Fall back to IRI path segments after the legislation ID (regex-extracted format)
  if (!sectionLabel) {
    const iriEnd = url.indexOf(iriKey) + iriKey.length;
    const pathSuffix = url.slice(iriEnd);
    if (pathSuffix) {
      sectionLabel = parseFragmentToLabel(pathSuffix);
    }
  }

  const legislationPart = `Zákon č. ${number}/${year} Z. z.`;
  const abbrPart = abbr ? ` (${abbr})` : '';
  const sectionPart = sectionLabel ? `, ${sectionLabel}` : '';
  return `${legislationPart}${abbrPart}${sectionPart}`;
}
